import { useEffect, useState, useCallback, useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Linking } from 'react-native';
import { useVerseRouteParams, replaceRoute } from '../hooks/useRouteParams';
import { useAppStore } from '../store/appStore';
import { fetchVerses as apiFetchVerses } from '../services/api';
import { getVerses as dbGetVerses, saveVerses as dbSaveVerses } from '../services/indexedDb';
import { formatNumber } from '../services/utils';
import { showSnackbar, hideSnackbar } from '../services/snackbar';
import { emitter, centerScrollViewRef, centerScrollOffset } from '../services/events';
import Basmala from '../components/Basmala';
import LoadingIndicator from '../components/LoadingIndicator';
import { reapplyNoSleep } from '../hooks/useNoSleep';
import { Fonts, FaIcons, MdiIcons, AyatEndGlyph } from '../styles/fonts';
import { useTheme } from '../styles/theme';


interface ProcessedVerse {
  ch_id: number;
  vs_id: number;
  footnotes: string[];
  [key: string]: unknown;
}

function processApiResponse(results: Array<Record<string, unknown>>): Record<string, ProcessedVerse> {
  const temp: Record<string, ProcessedVerse> = {};
  for (let i = 0; i < results.length; i++) {
    const item: ProcessedVerse = {
      ch_id: (results[i]._id as Record<string, number>).ch,
      vs_id: (results[i]._id as Record<string, number>).vs,
      footnotes: (results[i].footnotes as string[]) || [],
    };
    const verses = results[i].verses as Record<string, Record<string, string>>;
    const langKeys = Object.keys(verses);
    for (const lang of langKeys) {
      const transKeys = Object.keys(verses[lang]);
      for (const trans of transKeys) {
        item[`${lang}-${trans}`] = verses[lang][trans];
      }
    }
    const key = `${formatNumber(item.ch_id)}-${formatNumber(item.vs_id)}`;
    temp[key] = item;
  }
  return temp;
}

function VerseEndMarker({ vsId }: { vsId: number | string }) {
  const { colors } = useTheme();
  return (
    <View style={styles.verseNumberContainer}>
      <Text style={[styles.stopSign, { color: colors.secondaryText }]}>{AyatEndGlyph}</Text>
      <Text style={[styles.verseNumber, { color: colors.secondaryText }]}>{vsId}</Text>
    </View>
  );
}

export default function VersePage() {
  const { ch, vs, scrollParam } = useVerseRouteParams();
  const settings = useAppStore((s) => s.settings);
  const setSettings = useAppStore((s) => s.setSettings);
  const setAppTitle = useAppStore((s) => s.setAppTitle);
  const setOnVersePage = useAppStore((s) => s.setOnVersePage);
  const chapters = useAppStore((s) => s.chapters);
  const availableTranslations = useAppStore((s) => s.availableTranslations);
  const online = useAppStore((s) => s.online);
  const isMobileAgent = useAppStore((s) => s.isMobileAgent);
  const setShowNote = useAppStore((s) => s.setShowNote);
  const setVerseNoteTitle = useAppStore((s) => s.setVerseNoteTitle);
  const setVerseFootnotes = useAppStore((s) => s.setVerseFootnotes);
  const leftDrawerOpen = useAppStore((s) => s.leftDrawerOpen);
  const rightDrawerOpen = useAppStore((s) => s.rightDrawerOpen);

  const [verses, setVerses] = useState<Record<string, ProcessedVerse>>({});
  const [optionVisible, setOptionVisible] = useState<Record<string, boolean>>({});
  const [playingVerse, setPlayingVerse] = useState<{ ch: string; vs: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const scrollRestoredRef = useRef(false);
  const verseRefs = useRef<Record<string, any>>({});

  const chNum = ch ? parseInt(ch, 10) : 0;
  const chapter = chapters.find((c) => c.chapter_number === chNum);
  const maxVerses = chapter?.num_of_verses || 286;

  let startVs: number;
  let endVs: number;
  if (vs) {
    const parts = vs.split('-');
    startVs = parseInt(parts[0], 10);
    endVs = parts[1] !== undefined ? parseInt(parts[1], 10) : startVs;
  } else {
    startVs = 1;
    endVs = Math.min(20, maxVerses);
  }

  const nativeShareEnabled = Platform.OS === 'web'
    ? typeof navigator.share === 'function' && isMobileAgent
    : true; // On native, share is always possible

  const restoreScrollPosition = useCallback(() => {
    if (scrollRestoredRef.current || Platform.OS !== 'web') return;
    if (scrollParam) {
      scrollRestoredRef.current = true;
      setTimeout(() => {
        const scrollPane = document.getElementById('scroll-center-pane-helper');
        if (scrollPane) scrollPane.scrollTop = parseInt(scrollParam, 10);
      }, 60);
    }
  }, [scrollParam]);

  useEffect(() => {
    setOnVersePage(true);
    return () => { setOnVersePage(false); };
  }, [setOnVersePage]);

  useEffect(() => {
    if (ch) {
      setSettings({
        current_ch: ch,
        last_ch: ch,
        last_vs: vs || `${startVs}-${endVs}`,
        current_start_vs: startVs,
        current_end_vs: endVs,
      });
      if (chapter) {
        const titleText = `${chapter.chapter_number}. ${chapter.chapter_name_ar_english} [ ${chapter.num_of_verses} verses ]`;
        setAppTitle(titleText);
      }
    }
  }, [ch, vs, chapter]);

  const fetchFromApi = useCallback(async () => {
    if (!online) {
      hideSnackbar();
      showSnackbar("Offline! Can't fetch new translations", 5000);
      setLoading(false);
      return;
    }
    try {
      showSnackbar('Fetching translations', 0);
      const vsParam = `${startVs}-${Math.min(endVs, maxVerses)}`;
      const lnParam = [settings.current_script, ...settings.lang_array].join(',');
      const result = await apiFetchVerses(ch!, vsParam, lnParam);
      const processed = processApiResponse(result);
      setVerses((prev) => ({ ...prev, ...processed }));
      setLoading(false);
      loadedRef.current = true;
      hideSnackbar();
      reapplyNoSleep();
      restoreScrollPosition();
      dbSaveVerses(processed as Record<string, Record<string, unknown>>).catch(() => {});
    } catch {
      setLoading(false);
      hideSnackbar();
    }
  }, [ch, startVs, endVs, maxVerses, online, settings.current_script, settings.lang_array, restoreScrollPosition]);

  const loadVerses = useCallback(async () => {
    if (!ch) return;
    showSnackbar('Loading translations', 0);
    const fCh = formatNumber(chNum);
    const fStart = formatNumber(startVs);
    const fEnd = formatNumber(Math.min(endVs, maxVerses));

    try {
      const cached = await dbGetVerses(fCh, fStart, fEnd);
      if (cached && Object.keys(cached).length > 0) {
        setVerses((prev) => ({ ...prev, ...cached as Record<string, ProcessedVerse> }));
        setLoading(false);
        loadedRef.current = true;
        hideSnackbar();
        reapplyNoSleep();
        restoreScrollPosition();

        let needsFetch = false;
        for (let i = startVs; i <= Math.min(endVs, maxVerses); i++) {
          const key = `${fCh}-${formatNumber(i)}`;
          if (!cached[key]) { needsFetch = true; break; }
          for (const lang of settings.lang_array) {
            if (!(cached[key] as Record<string, unknown>)[lang]) { needsFetch = true; break; }
          }
          if (needsFetch) break;
        }
        if (needsFetch) fetchFromApi();
        return;
      }
    } catch { /* empty */ }

    fetchFromApi();
  }, [ch, chNum, startVs, endVs, maxVerses, settings.lang_array, settings.current_script, fetchFromApi, restoreScrollPosition]);

  useEffect(() => {
    if (!ch) return;
    loadedRef.current = false;
    scrollRestoredRef.current = false;
    setVerses({});
    setLoading(true);
    loadVerses();
  }, [ch, vs, loadVerses]);

  // Listen for recitation state events
  useEffect(() => {
    const handleRecitationState = (detail: { ch?: string | number; vs?: string | number; playing: boolean }) => {
      if (!detail.playing) {
        setPlayingVerse(null);
        return;
      }
      if (detail.ch !== undefined && detail.vs !== undefined) {
        setPlayingVerse({ ch: String(detail.ch), vs: String(detail.vs) });
      }
    };
    emitter.on('gv-recitation-state', handleRecitationState);
    return () => { emitter.off('gv-recitation-state', handleRecitationState); };
  }, []);

  // Auto-scroll to the playing verse in regular mode
  useEffect(() => {
    if (!playingVerse || settings.view_mode.value !== 'regular') return;
    if (String(playingVerse.ch) !== String(chNum)) return;

    const verseId = `${chNum}-${playingVerse.vs}`;

    if (Platform.OS === 'web') {
      const el = document.getElementById(verseId);
      const scrollContainer = document.getElementById('scroll-center-pane-helper') as any;
      if (el && scrollContainer) {
        const elTop = el.getBoundingClientRect().top + scrollContainer.scrollTop - scrollContainer.getBoundingClientRect().top - 10;
        scrollContainer.scrollTop = Math.max(0, elTop);
      }
    } else {
      const ref = verseRefs.current[verseId];
      if (ref && centerScrollViewRef.current) {
        ref.measure((_x: number, _y: number, _w: number, _h: number, _pageX: number, pageY: number) => {
          const scrollView = centerScrollViewRef.current;
          if (!scrollView) return;
          (scrollView as any).measure?.((_sx: number, _sy: number, _sw: number, _sh: number, _spx: number, scrollPageY: number) => {
            const targetY = centerScrollOffset.current + (pageY - scrollPageY) - 60;
            scrollView.scrollTo({ y: Math.max(0, targetY), animated: true });
          });
        });
      }
    }
  }, [playingVerse, settings.view_mode.value, chNum]);

  const loadMore = useCallback(
    (newStart: number, newEnd: number) => {
      const clampedEnd = Math.min(newEnd, maxVerses);
      replaceRoute(`/${ch}/${newStart}-${clampedEnd}`);
      setSettings({
        current_start_vs: Math.min(startVs, newStart),
        current_end_vs: clampedEnd,
      });

      const fCh = formatNumber(chNum);
      const fStart = formatNumber(newStart);
      const fEnd = formatNumber(clampedEnd);
      const lnParam = [settings.current_script, ...settings.lang_array].join(',');

      dbGetVerses(fCh, fStart, fEnd).then((cached) => {
        if (cached && Object.keys(cached).length > 0) {
          setVerses((prev) => ({ ...prev, ...cached as Record<string, ProcessedVerse> }));
        }
      }).catch(() => {});

      if (online) {
        apiFetchVerses(ch!, `${newStart}-${clampedEnd}`, lnParam).then((result) => {
          const processed = processApiResponse(result);
          setVerses((prev) => ({ ...prev, ...processed }));
          dbSaveVerses(processed as Record<string, Record<string, unknown>>).catch(() => {});
        }).catch(() => {});
      }
    },
    [ch, chNum, maxVerses, startVs, online, settings, setSettings],
  );

  const sortedVerseKeys = Object.keys(verses).sort();
  const toggleOption = (key: string) => {
    setOptionVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const showNoteModal = (chId: number, vsId: number) => {
    if (leftDrawerOpen || rightDrawerOpen) return;
    const key = `${formatNumber(chId)}-${formatNumber(vsId)}`;
    const verse = verses[key];
    if (verse?.footnotes?.length > 0 && chapter) {
      setVerseNoteTitle(`${chapter.chapter_number}. ${chapter.chapter_name_ar_english} : ${vsId}`);
      setVerseFootnotes(verse.footnotes);
      setShowNote(true);
    }
  };

  const playVerse = (chId: number | string, vsId: number | string) => {
    emitter.emit('gv-play-verse', { ch: chId, vs: vsId });
  };

  const openUrl = (url: string) => {
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(url);
    }
  };

  const nativeShare = useCallback((chId: number | string, vsId: number | string) => {
    const url = `https://app.guidedverses.xyz/${chId}/${vsId}`;
    const key = `${formatNumber(Number(chId))}-${formatNumber(Number(vsId))}`;
    const v = verses[key];
    let text = '';
    if (v) {
      if (settings.show_arabic) {
        text += String(v[settings.current_script] || '').trim() + '\n';
      }
      for (const lang of settings.lang_array) {
        const trans = String(v[lang] || '').replace(/<[^>]+>/g, '').trim();
        if (trans) text += '\n' + trans + '\n';
      }
    }
    if (Platform.OS === 'web' && navigator.share) {
      navigator.share({ title: 'Guided Verses', text, url }).catch(() => {});
    } else {
      Linking.openURL(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`);
    }
  }, [verses, settings]);

  const { colors } = useTheme();
  const fontSizeArabic = {
    fontSize: Number(settings.arabic_font_size),
    fontFamily: settings.arabic_font.value,
  };
  const fontSizeBody = { fontSize: Number(settings.trans_font_size) };
  const transFont = (langItem: string) => langItem.startsWith('bn-') ? Fonts.AponaLohit : undefined;
  const viewMode = settings.view_mode.value;
  const showBasmala = chNum !== 1 && chNum !== 9 && sortedVerseKeys.length > 0;
  const isEmpty = sortedVerseKeys.length === 0;

  return (
    <View style={styles.container}>
      {/* Basmala */}
      {showBasmala && (
        <View style={styles.basmalaContainer}>
          <Basmala />
        </View>
      )}

      {/* Offline Error */}
      {isEmpty && !online && (
        <View style={styles.centerView}>
          <Text style={{ fontSize: 48, marginBottom: 15 }}>⛓</Text>
          <Text style={[styles.errorText, { color: colors.primaryText }]}>Oppss... Please check your internet connection.</Text>
          <Pressable
            style={[styles.actionBtn, { backgroundColor: colors.topBarBg }]}
            onPress={() => { if (Platform.OS === 'web') window.location.reload(); }}
          >
            <Text style={[styles.actionBtnText, { color: colors.topBarIcon }]}>Retry</Text>
          </Pressable>
        </View>
      )}

      {/* Loading */}
      {isEmpty && online && loading && <LoadingIndicator />}

      {/* Regular View */}
      {viewMode === 'regular' && !isEmpty && (
        <View style={styles.verseList} nativeID="ayah-list">
          {sortedVerseKeys.map((key) => {
            const v = verses[key];
            const hasFootnotes = v.footnotes && v.footnotes.length > 0;
            const showOpt = optionVisible[key] || false;
            const isPlaying =
              playingVerse &&
              String(playingVerse.ch) === String(chNum) &&
              String(playingVerse.vs) === String(v.vs_id);

            return (
              <View
                key={key}
                ref={(r: any) => { verseRefs.current[`${chNum}-${v?.vs_id}`] = r; }}
                nativeID={`${chNum}-${v?.vs_id}`}
                style={styles.verseItem}
              >
                <View style={[
                  styles.verseCard,
                  { backgroundColor: isPlaying ? colors.selectedVerseBg : colors.cardBg },
                  hasFootnotes && { borderRightWidth: 7, borderRightColor: colors.cardHasFootnotes },
                ]}>
                  {/* Verse number */}
                  <View style={styles.verseNumberContainer}>
                    <Text style={styles.stopSign}>{AyatEndGlyph}</Text>
                    <Text style={styles.verseNumber}>{v?.vs_id}</Text>
                  </View>

                  {/* Options toggle */}
                  <Pressable
                    style={styles.optionDots}
                    onPress={() => toggleOption(key)}
                  >
                    <Text style={[styles.dotsText, { color: colors.optionDotsColor }]}>{MdiIcons.moreVert}</Text>
                  </Pressable>

                  {/* Options panel */}
                  {showOpt && (
                    <View style={styles.optionsPanel}>
                      <Pressable style={styles.optPlayBtn} onPress={() => playVerse(chNum, v.vs_id)}>
                        <Text style={[styles.optPlayIcon, { color: colors.faIconColor }]}>{isPlaying ? FaIcons.pause : FaIcons.play}</Text>
                      </Pressable>
                      <Text style={[styles.shareHeading, { color: colors.secondaryText }]}>Share this verse</Text>
                      <View style={styles.shareRow}>
                        <Pressable
                          style={[styles.shareBtn, styles.shareBtnFb]}
                          onPress={() => openUrl(`https://www.facebook.com/sharer/sharer.php?u=https://app.guidedverses.xyz/${chNum}/${v.vs_id}`)}
                        >
                          <Text style={styles.shareBtnIcon}>{FaIcons.facebook}</Text>
                        </Pressable>
                        <Pressable
                          style={[styles.shareBtn, styles.shareBtnX]}
                          onPress={() => openUrl(`https://twitter.com/intent/tweet?text=%40guidedverses&url=https://app.guidedverses.xyz/${chNum}/${v.vs_id}`)}
                        >
                          <Text style={[styles.shareBtnIcon, styles.shareBtnXIcon]}>𝕏</Text>
                        </Pressable>
                        <Pressable
                          style={[styles.shareBtn, styles.shareBtnIg]}
                          onPress={() => openUrl(`https://www.instagram.com/?url=https://app.guidedverses.xyz/${chNum}/${v.vs_id}`)}
                        >
                          <Text style={styles.shareBtnIcon}>{FaIcons.instagram}</Text>
                        </Pressable>
                        {nativeShareEnabled && (
                          <Pressable
                            style={[styles.shareBtn, { backgroundColor: colors.interactiveColor }]}
                            onPress={() => nativeShare(chNum, v.vs_id)}
                          >
                            <Text style={styles.shareBtnIcon}>{FaIcons.shareAlt}</Text>
                          </Pressable>
                        )}
                      </View>
                      <Pressable
                        style={[styles.embedBtn, { backgroundColor: colors.interactiveColor }]}
                        onPress={() => openUrl('https://widget.guidedverses.xyz/create/')}
                      >
                        <Text style={styles.embedBtnText}>EMBED IN YOUR WEBSITE</Text>
                      </Pressable>
                    </View>
                  )}

                  {/* Verse content */}
                  {!showOpt && (
                    <Pressable onPress={() => showNoteModal(chNum, v.vs_id)}>
                      {/* Arabic */}
                      {settings.show_arabic && (
                        <View style={styles.arabicBox}>
                          <Text style={[styles.arabicText, fontSizeArabic, { color: colors.arabicTextColor }]}>
                            {String(v[settings.current_script] || '')}
                          </Text>
                        </View>
                      )}

                      {/* Translations */}
                      {settings.lang_array.map((langItem) => (
                        <View key={langItem} style={styles.transBox}>
                          <Text style={[styles.transName, { color: colors.transNameColor }]}>
                            {availableTranslations[langItem]?.language || ''} - {availableTranslations[langItem]?.title || langItem}
                          </Text>
                          <View style={[styles.transHr, { backgroundColor: colors.hrColor }]} />
                          <Text style={[styles.transText, fontSizeBody, { color: colors.transTextColor, fontFamily: transFont(langItem) }]}>
                            {String(v[langItem] || '').replace(/<[^>]+>/g, '')}
                          </Text>
                        </View>
                      ))}

                      {/* Footnotes indicator */}
                      {hasFootnotes && (
                        <Text style={[styles.footnoteIndicator, { color: colors.secondaryText }]}>•••</Text>
                      )}
                    </Pressable>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {/* Mushaf View */}
      {viewMode === 'mushaf' && !isEmpty && (
        <View style={styles.mushafContainer} nativeID="recitation-container">
          <Text style={[styles.arabicText, fontSizeArabic, { textAlign: 'center', lineHeight: Number(settings.arabic_font_size) * 2.5, color: colors.arabicTextColor }]}>
            {sortedVerseKeys.map((key) => {
              const v = verses[key];
              if (!v) return null;
              const isVersePlaying =
                playingVerse &&
                String(playingVerse.ch) === String(chNum) &&
                String(playingVerse.vs) === String(v.vs_id);
              return (
                <Text key={key} style={isVersePlaying ? { backgroundColor: colors.selectedVerseBg } : undefined}>
                  {String(v[settings.current_script] || '') + ' '}
                  <VerseEndMarker vsId={v.vs_id} />
                </Text>
              );
            })}
          </Text>
        </View>
      )}

      {/* Single Card View */}
      {viewMode === 'single-card' && !isEmpty && (
        <View style={styles.singleCard}>
          <View style={[styles.verseCard, { backgroundColor: colors.cardBg }]}>
            <Text style={[styles.singleCardTitle, { color: colors.transTextColor }]}>
              Surah {chapter?.chapter_name_ar_english} [ {chNum}
              {vs && ` : ${vs.replace('-', ' - ')}`} ]
            </Text>

            {/* Arabic — flowing inline RTL paragraph */}
            {settings.show_arabic && (
              <View style={styles.arabicBox}>
                <Text style={[styles.arabicText, fontSizeArabic, { color: colors.arabicTextColor }]}>
                  {sortedVerseKeys.map((key) => {
                    const v = verses[key];
                    if (!v) return null;
                    const isVersePlaying =
                      playingVerse &&
                      String(playingVerse.ch) === String(chNum) &&
                      String(playingVerse.vs) === String(v.vs_id);
                    return (
                      <Text key={key} style={isVersePlaying ? { backgroundColor: colors.selectedVerseBg } : undefined}>
                        {String(v[settings.current_script] || '') + ' '}
                        <VerseEndMarker vsId={v.vs_id} />
                      </Text>
                    );
                  })}
                </Text>
              </View>
            )}

            {/* Translations — verse marker + text per verse per language */}
            {settings.lang_array.map((langItem) => (
              <View key={langItem} style={styles.transBox}>
                <Text style={[styles.transName, { color: colors.transNameColor }]}>
                  {availableTranslations[langItem]?.language || ''} - {availableTranslations[langItem]?.title || langItem}
                </Text>
                <View style={[styles.transHr, { backgroundColor: colors.hrColor }]} />
                <Text style={[styles.transText, fontSizeBody, { color: colors.transTextColor, lineHeight: Number(settings.trans_font_size) * 2.2, fontFamily: transFont(langItem) }]}>
                  {sortedVerseKeys.map((key) => {
                    const v = verses[key];
                    if (!v) return null;
                    const text = String(v[langItem] || '').replace(/<[^>]+>/g, '');
                    const isVersePlaying =
                      playingVerse &&
                      String(playingVerse.ch) === String(chNum) &&
                      String(playingVerse.vs) === String(v.vs_id);
                    return (
                      <Text key={key} style={isVersePlaying ? { backgroundColor: colors.selectedVerseBg } : undefined}>
                        <VerseEndMarker vsId={v.vs_id} />
                        {text + ' '}
                      </Text>
                    );
                  })}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Load more buttons */}
      {!isEmpty && (
        <View style={styles.buttonRow}>
          <Pressable style={[styles.actionBtn, { backgroundColor: colors.recitationBg }]} onPress={() => loadMore(1, 300)}>
            <Text style={[styles.actionBtnText, { color: colors.recitationCtrlIcon }]}>Full Chapter</Text>
          </Pressable>
          <Pressable style={[styles.actionBtn, { backgroundColor: colors.recitationBg }]} onPress={() => loadMore(endVs + 1, endVs + 20)}>
            <Text style={[styles.actionBtnText, { color: colors.recitationCtrlIcon }]}>Load More</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 10,
  },
  basmalaContainer: {
    alignItems: 'center',
  },
  centerView: {
    alignItems: 'center',
    paddingVertical: 50,
  },
  errorText: {
    fontSize: 16,
    marginBottom: 20,
  },
  verseList: {
    paddingHorizontal: 12,
  },
  verseItem: {
    marginBottom: 15,
  },
  verseCard: {
    borderRadius: 2,
    padding: 10,
    // @ts-expect-error web shadow
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
  },
  hasFootnotes: {
    borderRightWidth: 7,
  },
  verseNumberContainer: {
    alignSelf: 'center',
    width: 46,
    height: 28,
    marginTop: 5,
  },
  stopSign: {
    fontFamily: Fonts.Scheherazade,
    fontSize: 46,
    lineHeight: 46,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
  verseNumber: {
    fontSize: 12,
    position: 'absolute',
    top: -3,
    left: 0,
    right: 0,
    lineHeight: 46,
    textAlign: 'center',
  },
  optionDots: {
    position: 'absolute' as any,
    right: 8,
    top: 8,
    padding: 4,
  },
  dotsText: {
    fontFamily: Fonts.MDI,
    fontSize: 22,
    color: '#ccc',
  },
  optionsPanel: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  optPlayBtn: {
    marginBottom: 12,
    padding: 8,
  },
  optPlayIcon: {
    fontFamily: Fonts.FA,
    fontSize: 28,
  },
  shareHeading: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 16,
  },
  shareRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  shareBtn: {
    width: 52,
    height: 52,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shareBtnFb: { backgroundColor: '#3B5998' },
  shareBtnX:  { backgroundColor: '#000000' },
  shareBtnIg: { backgroundColor: '#C13584' },
  shareBtnIcon: {
    fontFamily: Fonts.FA,
    fontSize: 22,
    color: '#fff',
  },
  shareBtnXIcon: {
    fontFamily: undefined, // plain text for the DoubleStruck X
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  embedBtn: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 2,
    // @ts-expect-error web shadow
    boxShadow: '2px 2px 4px 0 rgba(0,0,0,.26)',
  },
  embedBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    // @ts-expect-error web text-transform
    letterSpacing: 1,
  },
  arabicBox: {
    paddingVertical: 10,
  },
  arabicText: {
    fontFamily: 'me_quran',
    fontSize: 28,
    lineHeight: 70,
    textAlign: 'center',
    // @ts-expect-error web direction
    direction: 'rtl',
  },
  transBox: {
    paddingVertical: 8,
  },
  transName: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 4,
    textAlign: 'center',
  },
  transHr: {
    height: 1,
    marginBottom: 6,
  },
  transText: {
    fontSize: 14,
    lineHeight: 24,
    textAlign: 'center',
  },
  footnoteIndicator: {
    fontSize: 24,
    textAlign: 'center',
    paddingVertical: 8,
  },
  mushafContainer: {
    padding: 20,
  },
  singleCardVerse: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  singleCard: {
    paddingHorizontal: 12,
    marginBottom: 15,
  },
  singleCardTitle: {
    margin: 15,
    textAlign: 'center',
    fontWeight: '600',
    fontSize: 14,
  },
  verseMarker: {
    fontFamily: Fonts.Scheherazade,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
    paddingHorizontal: 12,
    gap: 12,
  },
  actionBtn: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 2,
    // @ts-expect-error web shadow
    boxShadow: '2px 2px 4px 0 rgba(0,0,0,.26)',
  },
  actionBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    // @ts-expect-error web text-transform
    textTransform: 'uppercase',
  },
});
